/* eslint sort-keys: "error" */
import type { TOC } from '@ember/component/template-only';
import { assert } from '@ember/debug';
import { concat } from '@ember/helper';
import { trustHTML } from '@ember/template';
import { ICONS_DEFAULT_MAP } from '@mylighthouse/prism-foundation';

import type {
  IconColor,
  IconName,
  IconSize,
} from '../../types/components/prism/icon.ts';
import { UNIQUE_ID, uniqueId } from '../../utils/unique-id.ts';

export const COLOR_CLASS_MAP: Record<IconColor, string> = {
  brand: 'prism-icon--brand',
  critical: 'prism-icon--critical',
  default: 'prism-icon--default',
  'default-inverse': 'prism-icon--default-inverse',
  disabled: 'prism-icon--disabled',
  emphasis: 'prism-icon--emphasis',
  info: 'prism-icon--info',
  inherit: 'prism-icon--inherit',
  intelligence: 'prism-icon--intelligence',
  'on-brand': 'prism-icon--on-brand',
  'on-critical': 'prism-icon--on-critical',
  'on-info': 'prism-icon--on-info',
  'on-intelligence': 'prism-icon--on-intelligence',
  'on-success': 'prism-icon--on-success',
  'on-warning': 'prism-icon--on-warning',
  subdued: 'prism-icon--subdued',
  success: 'prism-icon--success',
  warning: 'prism-icon--warning',
} as const;

export const SIZE_CLASS_MAP: Record<IconSize, string> = {
  'extra-small': 'prism-icon--extra-small',
  large: 'prism-icon--large',
  medium: 'prism-icon--medium',
  small: 'prism-icon--small',
} as const;

function getClassForColor(color?: IconColor): string {
  return COLOR_CLASS_MAP[color ?? 'default'];
}

function getClassForSize(size?: IconSize): string {
  return SIZE_CLASS_MAP[size ?? 'medium'];
}

function getSvgCode(name: IconName): string {
  let svgCode = ICONS_DEFAULT_MAP[name];

  assert(`Icon "${name}" does not exist`, svgCode);

  if (svgCode === undefined) {
    return '';
  }

  /*
   * Make sure that multiple SVGs on the same page don't collide with each other
   * by replacing the hardcoded prefix (see svgo config files) with a unique one.
   */
  svgCode = svgCode.replaceAll(UNIQUE_ID, uniqueId());

  return svgCode;
}

interface PrismIconSignature {
  Args: {
    color?: IconColor;
    name: IconName;
    size?: IconSize;
  };
  Element: HTMLSpanElement;
}

const PrismIcon: TOC<PrismIconSignature> = <template>
  <span
    class={{concat
      "prism-icon"
      " "
      (getClassForSize @size)
      " "
      (getClassForColor @color)
    }}
    data-test-icon={{@name}}
    ...attributes
  >
    {{trustHTML (getSvgCode @name)}}
  </span>
</template>;

export default PrismIcon;

export type { IconColor, IconName, IconSize };
